import React, { memo, useCallback } from 'react';

import { Box } from '@mui/material';
import {
  AutocompleteCDS,
  CheckboxCDS,
  TextFieldCDS,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useFormContext from '../hooks/useFormContext';
import { wrapperClassname } from '../LoanActionItem.constants';
import { getOptionLabel, isOptionEqual } from '../LoanActionSelectOption.utils';

import {
  getSnapdocsCheckboxConfigs,
  getSnapdocsSelectConfigs,
  snapdocsTextFieldConfigs,
} from './LoanActionSnapdocsRow.config';
import {
  EMPTY_DOCUMENT_TYPES,
  EMPTY_FIELDS,
  PROMPT_AT_EXECUTION_PLACEHOLDER,
  SnapdocsFieldEnum,
  snapdocsFieldsByActionType,
} from './LoanActionSnapdocsRow.constants';

const LoanActionSnapdocsRow = ({
  itemType,
  rootPath,
  isDisabled,
  isEditAllowed,
  isAutomatedActions = false,
  documentTypesOptions = EMPTY_DOCUMENT_TYPES,
}) => {
  const { form, validationErrors, setMultipleInForm, validateField } = useFormContext();

  const snapdocsClosing = _.get(form, rootPath) ?? {};
  const fields = snapdocsFieldsByActionType[itemType] ?? EMPTY_FIELDS;
  const disabled = isDisabled || !isEditAllowed;
  const isPromptAtExecution = !isAutomatedActions && !!snapdocsClosing.promptAtExecution;
  const commentTextPath = `${rootPath}.${SnapdocsFieldEnum.TEXT}`;
  const hasCommentText = fields.includes(SnapdocsFieldEnum.TEXT);
  const selectConfigs = getSnapdocsSelectConfigs({
    rootPath,
    fields,
    snapdocsClosing,
    documentTypesOptions,
    validationErrors,
  });

  const setFieldValue = useCallback(
    (path, value) => {
      setMultipleInForm({ [path]: value });
      validateField?.(path, value);
    },
    [setMultipleInForm, validateField],
  );

  const getSelectChangeHandler = useCallback(
    (path, emptyValue) => (__, value) => setFieldValue(path, value ?? emptyValue),
    [setFieldValue],
  );

  const onTextFieldChange = useCallback(
    event => setFieldValue(event.target.name, event.target.value),
    [setFieldValue],
  );

  const onCheckboxChange = useCallback(
    event => setFieldValue(event.target.name, event.target.checked),
    [setFieldValue],
  );

  const onPromptAtExecutionChange = useCallback(
    event => {
      setMultipleInForm({ [event.target.name]: event.target.checked });

      if (hasCommentText) {
        validateField?.(commentTextPath, _.get(form, commentTextPath));
      }
    },
    [setMultipleInForm, validateField, hasCommentText, commentTextPath, form],
  );

  return (
    <Box className={wrapperClassname}>
      {selectConfigs.map(
        ({
          field,
          path,
          label,
          placeholder,
          isMultiple,
          isRequired,
          isHidden,
          options,
          value,
          emptyValue,
          error,
          helperText,
          dataTestId,
        }) => (
          <AutocompleteCDS
            dataTestId={dataTestId}
            disabled={disabled}
            error={error}
            getOptionLabel={getOptionLabel}
            helperText={helperText}
            isHidden={isHidden}
            isOptionEqualToValue={isOptionEqual}
            key={field}
            label={label}
            multiple={isMultiple}
            name={path}
            options={options}
            placeholder={placeholder}
            required={isRequired}
            value={value}
            onChange={getSelectChangeHandler(path, emptyValue)}
          />
        ),
      )}
      {snapdocsTextFieldConfigs.map(
        ({ field, label, placeholder, isMultiline, rows, isRequired, isPromptable, getTestId }) => {
          const isPrompted = !!isPromptable && isPromptAtExecution;

          return (
            <TextFieldCDS
              dataTestId={getTestId(rootPath)}
              disabled={disabled || isPrompted}
              error={!!_.get(validationErrors, `${rootPath}.${field}`)}
              helperText={_.get(validationErrors, `${rootPath}.${field}`)}
              isHidden={!fields.includes(field)}
              key={field}
              label={label}
              multiline={isMultiline}
              name={`${rootPath}.${field}`}
              placeholder={isPrompted ? PROMPT_AT_EXECUTION_PLACEHOLDER : placeholder}
              required={isRequired && !isPrompted}
              rows={rows}
              value={snapdocsClosing[field] ?? ''}
              onChange={onTextFieldChange}
            />
          );
        },
      )}
      {getSnapdocsCheckboxConfigs(itemType).map(({ field, label, getTestId }) => (
        <CheckboxCDS
          dataTestId={getTestId(rootPath)}
          disabled={disabled}
          isHidden={
            !fields.includes(field) ||
            (isAutomatedActions && field === SnapdocsFieldEnum.PROMPT_AT_EXECUTION)
          }
          key={field}
          label={label}
          name={`${rootPath}.${field}`}
          value={!!snapdocsClosing[field]}
          onChange={
            field === SnapdocsFieldEnum.PROMPT_AT_EXECUTION
              ? onPromptAtExecutionChange
              : onCheckboxChange
          }
        />
      ))}
    </Box>
  );
};

LoanActionSnapdocsRow.propTypes = {
  itemType: PropTypes.string.isRequired,
  rootPath: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isAutomatedActions: PropTypes.bool,
  documentTypesOptions: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, value: PropTypes.string }),
  ),
};

export default memo(LoanActionSnapdocsRow);
